import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { useSectionAnimation } from '@/hooks/use-section-animation';

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  className?: string;
  iconClassName?: string;
  theme?: 'light' | 'dark';
  delay?: number;
}

export const FeatureCard = ({
  icon: Icon,
  title,
  description,
  className,
  iconClassName,
  theme = 'light',
  delay = 0,
}: FeatureCardProps) => {
  const animation = useSectionAnimation({ 
    direction: 'up',
    delay,
    once: false
  });

  return (
    <motion.div
      ref={animation.ref}
      initial={animation.initial}
      animate={animation.animate}
      transition={animation.transition}
      className={cn(
        'flex gap-4 items-start p-4 rounded-lg transition-colors duration-200',
        theme === 'light' 
          ? 'hover:bg-gray-50' 
          : 'hover:bg-gray-800/50',
        className
      )}
    >
      <div 
        className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
          theme === 'light'
            ? 'bg-[#2E7D32]/10'
            : 'bg-[#4CAF50]/20',
          iconClassName
        )}
      >
        <Icon 
          className={cn(
            'w-5 h-5',
            theme === 'light' ? 'text-[#2E7D32]' : 'text-[#4CAF50]'
          )} 
        />
      </div>
      <div>
        <h3 
          className={cn(
            'font-semibold mb-1',
            theme === 'light' ? 'text-gray-900' : 'text-white'
          )}
        >
          {title}
        </h3>
        <p 
          className={cn(
            'text-sm leading-relaxed',
            theme === 'light' ? 'text-gray-600' : 'text-gray-300'
          )}
        >
          {description}
        </p>
      </div>
    </motion.div>
  );
};
